/** @jsx jsx */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import ReactQuill from 'react-quill'
import MagicUrl from 'quill-magic-url'
import TextFieldBase, { TextFieldBaseProps, useFocusState } from '../TextFieldBase/TextFieldBase'
import 'react-quill/dist/quill.snow.css'
import { stripHtml } from '../../../../lib/utils'
import useOnUnmount from '../../../../lib/hooks/useOnUmount'
import { twentyFourDpShadowCss } from '../../../../lib/styles/shadows'

/**
 * Registering MagicUrl Quilljs module. This module handles typing/pasting URLs, transforming them into hyperlinks automatically.
 */
ReactQuill.Quill.register('modules/magicUrl', MagicUrl)

/**
 * This class is a Quilljs Custom Module for handle the maxlength property.
 * Quilljs doesn't have a native maxlength control, so this implementation avoids typing/pasting a text bigger than the [maxlength] property value
 * if it's present/valid in Quilljs modules configuration.
 */
export class MaxLengthModule {
  constructor(private quill: any, private value: number) {
    quill.on('text-change', () => {
      if (this.value) {
        const countBreaks = (this.quill.getText()?.match(/\n/g) || []).length - 1 // quilljs always put a \n at the end, so it's mandatory -1
        const maxlength = this.value + countBreaks
        if (this.quill.getLength() >= maxlength) {
          quill.deleteText(maxlength, this.quill.getLength())
        }
      }
    })
  }
}
ReactQuill.Quill.register('modules/maxlength', MaxLengthModule)

export type RichTextChangeEvent = {
  target: {
    value: string
    name: string
  }
}

export type RichTextFieldProps = {
  /** Compliant aria-label */
  ariaLabel?: string
  /** Whether the editor is disabled */
  disabled?: boolean
  /** name of field. Returned with onChange callback */
  name?: string
  /**
   * Event callback when control has blurred.
   * Because Textfield could be a input or textarea, the callback needs to be "any" */
  onBlur?: (event: any) => void
  /** Event callback text has changed. */
  onChange: (event: string) => void
  /**
   * Event callback when control has focused.
   * Because Textfield could be a input or textarea, the callback needs to be "any" */
  onFocus?: (event: any) => void
  /** Placeholder text */
  placeholder?: string
  /** Number of rows */
  rows: number
  /** Value of editor */
  value?: string
  /** Used for focus/cursor handling (useFocus hook) */
  inputRef?: any
} & Omit<TextFieldBaseProps, 'inputComponent' | 'focused'>

const defaultProps: Pick<
  RichTextFieldProps,
  'ariaLabel' | 'disabled' | 'inputRef' | 'name' | 'onBlur' | 'onFocus' | 'placeholder' | 'value'
> = {
  ariaLabel: undefined,
  disabled: false,
  inputRef: undefined,
  name: undefined,
  onBlur: undefined,
  onFocus: undefined,
  placeholder: undefined,
  value: ''
}

/**
 * Component for handling rich text fields.
 */
const RichTextField = forwardRef(
  (
    {
      ariaLabel,
      disabled,
      id,
      label,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      rows,
      value,
      ...props
    }: RichTextFieldProps,
    inputRef: any
  ) => {
    const editorRef = useRef<ReactQuill>(null)
    const [toolbarDisplay, setToolbarDisplay] = useState('none')
    const [focused, onFocusHook, onBlurHook] = useFocusState(onFocus, onBlur, disabled)

    useImperativeHandle(inputRef, () => editorRef.current)

    // Using this custom hook to call onBlur when component umount, because Quilljs doesn't do it
    useOnUnmount(() => {
      if (focused) {
        onBlurHook({ target: editorRef.current?.editor })
      }
    }, [onBlurHook])

    // Handle keyboard module
    const initKeyboardModule = useCallback(() => {
      const keyboardModule = editorRef.current?.editor?.getModule('keyboard')
      if (keyboardModule) {
        // Register alt+enter for open link
        keyboardModule.bindings[13].unshift({
          key: 13,
          altKey: true,
          handler: (range: any, context: any) => {
            if (context.format?.link) {
              window.open(context?.format?.link, '_blank')?.focus()
            }
            return true
          }
        })

        // Unregister tab: Quilljs handles tab/shift+tab by default to insert/remove tab character in text, but we want tab to navigate between elements
        keyboardModule.bindings[9].splice(0, keyboardModule.bindings[9].length)
      }
    }, [editorRef])

    useEffect(() => {
      initKeyboardModule()
    }, [initKeyboardModule])

    const editorCss = css`
      .ql-container.ql-snow {
        border: none;
      }

      .ql-container {
        font-family: inherit;
        font-size: inherit;
      }

      .ql-editor {
        padding: 0;
        line-height: 20px;
        min-height: ${rows * 20}px;
      }

      .ql-snow .ql-editor a {
        text-decoration: inherit;
      }

      .ql-editor.ql-blank::before {
        font-style: normal;
        left: 0;
        top: 0;
      }

      .ql-toolbar.ql-snow,
      .ql-snow .ql-tooltip {
        border: 1px solid rgb(204, 204, 204);
        background: rgb(255, 255, 255);
        border-radius: 4px;
        ${twentyFourDpShadowCss}
      }

      .ql-snow .ql-tooltip {
        top: 0 !important;
        left: 0 !important;
        z-index: 1;
      }
    `

    // setups an overlay tollbar at the corner top to manage hyperlinks on selected texts
    const toolbarCss = css`
      .ql-toolbar {
        position: absolute;
        top: 4px;
        left: 4px;
        z-index: 1;
        background: #fff;
        display: ${toolbarDisplay};
      }
    `

    const handleChange = (
      newValue: string,
      _delta: any,
      _source: any,
      editor: ReactQuill.UnprivilegedEditor
    ) => {
      if (newValue !== value) {
        // When the input content is empty QuillJS keeps this html code "<p><br /></p>" causing
        // validation issues so we do need to handle it manually returning an empty space.
        // Reference: https://github.com/quilljs/quill/issues/1235

        // Gets the contents as plan text to check if it's empty
        if (!editor.getText()?.trim()) {
          onChange('')
        } else {
          onChange(newValue)
        }
      }
    }

    const handleChangeSelection = (event: ReactQuill.Range) => {
      if (!disabled && event && event.length > 0) {
        setToolbarDisplay('block')
      } else {
        setToolbarDisplay('none')
      }
    }

    const formats: string[] = ['link']

    const { maxLength } = props

    const modules: any = {
      toolbar: ['link'],
      clipboard: {
        matchVisual: false
      },
      magicUrl: true,
      maxlength: maxLength
    }
    const disabledStyles = disabled ? { backgroundColor: '#f9f9f9', cursor: 'default' } : null
    const styles = {
      minHeight: `${rows * 20}px`,
      ...disabledStyles
    }

    const editorComponent = (
      <ReactQuill
        id={id}
        className="quill-editor"
        ref={editorRef}
        value={value}
        onChange={handleChange}
        formats={formats}
        onBlur={(range, sources, editor) => onBlurHook({ target: editor })}
        onFocus={onFocusHook}
        onChangeSelection={handleChangeSelection}
        modules={modules}
        css={[editorCss, toolbarCss]}
        style={styles}
        readOnly={disabled}
        placeholder={placeholder}
      />
    )

    // strip the html so we can correctly calculate the remaining characters
    const strippedValue = stripHtml(value) || ''
    return (
      <TextFieldBase
        {...props}
        id={id}
        focused={focused}
        inputComponent={editorComponent}
        label={label}
        placeholder={placeholder}
        value={strippedValue}
      />
    )
  }
)

RichTextField.defaultProps = defaultProps

export default RichTextField
